import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function DocenteHorario() {
    const [tipoHorario, setTipoHorario] = useState('preadmision'); // preadmision | matriculacion
    const [gestion, setGestion] = useState(new Date().getFullYear());

    // Generar horas desde 08:00 hasta 18:30 con intervalos de 30 min     
    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 6; hour <= 18; hour++) {
            slots.push(`${hour.toString().padStart(2, '0')}:00`);
            slots.push(`${hour.toString().padStart(2, '0')}:30`);
        }
        return slots;
    };

    const timeSlots = generateTimeSlots();
    const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
    const [horarios, setHorarios] = useState([]);
    const [loading, setLoading] = useState(false);

    // Helper para normalizar el grid (dia-hora -> clase)
    const getScheduleGrid = () => {
        const grid = {};
        horarios.forEach(h => {
            if (!h.hora_inicio || !h.hora_fin || !h.dia_semana) return;
            
            const diasRaw = Array.isArray(h.dia_semana) ? h.dia_semana : [h.dia_semana];
            diasRaw.forEach(diaRaw => {
                const dia = diaRaw.charAt(0).toUpperCase() + diaRaw.slice(1).toLowerCase();
                const [hIni, mIni] = h.hora_inicio.substring(0, 5).split(':').map(Number);
                const [hFin, mFin] = h.hora_fin.substring(0, 5).split(':').map(Number);
                const startMins = hIni * 60 + mIni;
                const endMins = hFin * 60 + mFin;
                const durationSlots = Math.ceil((endMins - startMins) / 30);

                if (durationSlots <= 0) return;

                const startSlot = `${hIni.toString().padStart(2, '0')}:${mIni.toString().padStart(2, '0')}`;
                const key = `${dia}-${startSlot}`;

                // Solo guardamos si no hay colisión o si es el primero
                if (!grid[key]) {
                    grid[key] = { ...h, rowSpan: durationSlots, isStart: true };
                    // Marcar slots siguientes como ocupados
                    for (let i = 1; i < durationSlots; i++) {
                        const totalMins = startMins + i * 30;
                        const nextSlotH = Math.floor(totalMins / 60);
                        const nextSlotM = totalMins % 60;
                        const nextSlot = `${nextSlotH.toString().padStart(2, '0')}:${nextSlotM.toString().padStart(2, '0')}`;
                        grid[`${dia}-${nextSlot}`] = { isContinuation: true };
                    }
                }
            });
        });
        return grid;
    };

    const scheduleGrid = getScheduleGrid();

    // Función para obtener el rango del slot (ej: "07:00 - 07:30")
    const getSlotRango = (horaInicio) => {
        const [h, m] = horaInicio.split(':').map(Number);
        let finH = h;
        let finM = m + 30;
        if (finM >= 60) {
            finM = 0;
            finH += 1;
        }
        const horaFin = `${finH.toString().padStart(2, '0')}:${finM.toString().padStart(2, '0')}`;
        return `${horaInicio} - ${horaFin}`;
    };

    useEffect(() => {
        loadData();
    }, [tipoHorario, gestion]);

    const loadData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/horarios?gestion=${gestion}&tipo=${tipoHorario}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setHorarios(data.horarios || []);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const getCellContent = (day, time) => {
        const [slotH, slotM] = time.split(':').map(Number);
        const slotMinutos = slotH * 60 + slotM;

        const item = horarios.find(h => {
             const diaMatch = h.dia_semana?.toLowerCase() === day.toLowerCase() || 
                             (Array.isArray(h.dia_semana) && h.dia_semana.some(d => d.toLowerCase() === day.toLowerCase()));
             
             if (!diaMatch || !h.hora_inicio || !h.hora_fin) return false;

             const [iniH, iniM] = h.hora_inicio.split(':').map(Number);
             const [finH, finM] = h.hora_fin.split(':').map(Number);
             const iniMin = iniH * 60 + iniM;
             const finMin = finH * 60 + finM;

             return slotMinutos >= iniMin && slotMinutos < finMin;
        });

        if (!item) return null;

        // Si es el inicio del bloque, mostramos información. Si no, solo el color de fondo.
        const [iniH, iniM] = item.hora_inicio.split(':').map(Number);
        const isStart = (iniH === slotH && iniM === slotM);

        return (
            <div 
                className={`text-[10px] md:text-xs px-1.5 h-full w-full rounded-none flex flex-col justify-center shadow-inner border-l-2`}
                style={{ 
                    backgroundColor: item.color_docente || '#DBEAFE',
                    borderColor: 'rgba(0,0,0,0.1)'
                }}
            >
                {isStart && (
                    <>
                        <div className="font-bold text-blue-900 line-clamp-1">{item.asignatura_nombre || item.codigo}</div>
                        <div className="text-blue-800 opacity-80 text-[9px]">P: {item.paralelo} | S: {item.sala}</div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-full mx-auto">
                <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4">
                    <h2 className="text-2xl font-bold mb-4">Horario General</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Horario</label>
                            <select
                                value={tipoHorario}
                                onChange={(e) => setTipoHorario(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                            >
                                <option value="preadmision">Curso de Preadmisión</option>
                                <option value="matriculacion">Licenciatura</option>
                                <option value="admision">Admisión (PAA)</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Gestión</label>
                            <select
                                value={gestion}
                                onChange={(e) => setGestion(parseInt(e.target.value))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                            >
                                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {loading ? <div className="p-8 text-center">Cargando...</div> : (
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-blue-600 text-white">
                                        <th className="border border-blue-700/30 p-2 text-xs font-semibold min-w-[140px] sticky left-0 bg-blue-600">Intervalo</th>
                                        {days.map(day => (
                                            <th key={day} className="border border-blue-700/30 p-2 text-xs font-semibold min-w-[160px]">{day}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {timeSlots.map((time, idx) => (
                                        <tr key={time} className="hover:bg-gray-50 h-10">
                                            <td className="border border-blue-700/10 p-2 text-[10px] md:text-xs font-bold text-gray-600 bg-gray-50 sticky left-0 min-w-[140px] whitespace-nowrap">
                                                {getSlotRango(time)}
                                            </td>
                                            {days.map(day => {
                                                const cellData = scheduleGrid[`${day}-${time}`];
                                                
                                                if (cellData?.isContinuation) return null;

                                                if (cellData?.isStart) {
                                                    return (
                                                        <td 
                                                            key={`${day}-${time}`} 
                                                            className="border border-blue-700/10 p-0 align-top h-full"
                                                            rowSpan={cellData.rowSpan}
                                                        >
                                                            <div 
                                                                className="px-2 py-1.5 h-full w-full min-h-[40px] border-l-4 shadow-inner flex flex-col justify-center overflow-hidden"
                                                                style={{ 
                                                                    backgroundColor: cellData.color_docente || '#EBF5FF',
                                                                    borderColor: 'rgba(0,0,0,0.1)'
                                                                }}
                                                            >
                                                                <div className="font-bold text-blue-900 line-clamp-2 text-[11px] leading-tight mb-0.5">
                                                                    {cellData.asignatura_nombre || cellData.codigo}
                                                                </div>
                                                                <div className="text-blue-800 text-[9px] opacity-80 font-medium">
                                                                    P: {cellData.paralelo} | S: {cellData.sala}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    );
                                                }

                                                return (
                                                    <td key={`${day}-${time}`} className="border border-blue-700/10 p-0 h-10"></td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
